import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { makeUrl, safeJoinWithSpace } from "@uplift-ltd/strings";
import React, { useCallback } from "react";
import { CourseProgressEnum } from "__generated__/globalTypes";
import Avatar from "components/common/Avatar";
import Button from "components/common/Button";
import CourseBadge from "components/common/CourseBadge";
import CreditHoursBadge from "components/common/CreditHoursBadge/CreditHoursBadge";
import Link from "components/common/Link";
import { COURSE_URL, SUBSCRIPTIONS_URL } from "constants/urls";
import { mapNodes } from "helpers/mapNodes";
import { formatUSD } from "helpers/strings";
import { useAnchorRef } from "hooks/useAnchorRef";
import useRouterNavigation from "hooks/useRouterNavigation";
import { CourseMaterialsForCatalog } from "../CourseMaterials";
import {
  CourseCatalogQueryAuthors as CourseCatalogQueryAuthor,
  CourseCatalogQueryCourseFields,
  CourseCatalogQueryFulfillsRequirements as CourseCatalogFulfillsRequirements,
} from "./__generated__/CourseCatalog";
import styles from "./Course.module.css";

interface CourseProps {
  title: string | null;
  slug: string | null;
  priceUSD: number;
  fulfillsRequirements: CourseCatalogFulfillsRequirements | null;
  materials: string[];
  authors: CourseCatalogQueryAuthor[];
  ceUnits: number;
  isEnrolled: boolean;
  isNew: boolean | null;
  isRecentlyUpdated: boolean | null;
  courseFields: CourseCatalogQueryCourseFields | null;
  isMembersExclusive: boolean;
}

const DateBadges = ({
  isNew,
  isRecentlyUpdated,
}: {
  isNew: boolean | null;
  isRecentlyUpdated: boolean | null;
}) => {
  return (
    <>
      {isRecentlyUpdated && <CourseBadge size="sm" type="updated" />}{" "}
      {!isRecentlyUpdated && isNew && <CourseBadge size="sm" type="new" />}
    </>
  );
};

const Course: React.FC<CourseProps> = ({
  title,
  slug: courseSlug,
  priceUSD,
  authors,
  ceUnits,
  fulfillsRequirements,
  courseFields,
  materials,
  isEnrolled,
  isNew,
  isRecentlyUpdated,
  isMembersExclusive,
}) => {
  // satisfy ts
  const slug = courseSlug || "";

  const ref = useAnchorRef<HTMLDivElement>(slug);
  const { push } = useRouterNavigation();
  const onClickTakeCourse = useCallback(() => {
    push(makeUrl(COURSE_URL, { slug, page: CourseProgressEnum.OVERVIEW.toLocaleLowerCase() }));
  }, [push, slug]);
  const requirements = mapNodes(fulfillsRequirements).map(item => item.name);

  return (
    <div className={styles.course} id={slug || undefined} ref={ref}>
      <div className={styles.courseDetails}>
        <Link
          as={makeUrl(COURSE_URL, { slug, page: "" })}
          href="/courses/[slug]"
          className={styles.courseTitleLink}
        >
          <h3 className={styles.courseTitle}>
            {title}
            {courseFields?.displayDateBadges && (
              <>
                {" "}
                <DateBadges isNew={isNew} isRecentlyUpdated={isRecentlyUpdated} />
              </>
            )}
          </h3>
        </Link>
        <div className={styles.courseMeta}>
          <Avatar extended authors={authors} />
          {materials.length > 0 ? (
            <div className={styles.courseMaterials}>
              <CourseMaterialsForCatalog
                materials={materials}
                extraText={courseFields?.courseMaterialsExtraText}
              />
            </div>
          ) : null}
          {requirements.length > 0 ? (
            <div className={styles.fulfillsRequirements}>
              Fulfills Requirement:
              <span className={styles.requirements}> {safeJoinWithSpace(requirements)}</span>
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.coursePurchase}>
        <div>
          <CreditHoursBadge ceUnits={ceUnits} />
        </div>
        <div className={styles.coursePriceAndButtonContainer}>
          <div className={styles.coursePrice}>{formatUSD(priceUSD)}</div>
          {isMembersExclusive && (
            <div className={styles.membersOnly}>
              <Link href={makeUrl(SUBSCRIPTIONS_URL, { slug: "" })}>Members Only</Link>
            </div>
          )}
          <div className={styles.courseButtonContainer}>
            <Button
              theme={isEnrolled ? "primary" : "light"}
              className={styles.courseButton}
              onClick={onClickTakeCourse}
              icon={!!isEnrolled && <FontAwesomeIcon icon={faCheck} className="mr-1" />}
            >
              {isEnrolled ? "Enrolled" : "Learn More"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Course;
